'use client';
/* eslint-disable @next/next/no-img-element -- collections prototype: <img> for R2/idb blob URLs, not the app image pipeline */ // ---------------------------------------------------------------------------
// Collection inspector — the RIGHT-side edit surface for one collection.
//
// Reads the persistent library store: a collection is resolved by id, its
// assets are the store records whose collectionId matches. Every edit —
// rename, status, attribute, import, move, delete, hero — writes straight to
// the store, so it is real and survives reload. Selection is a Set of asset
// ids shared across the three views (Gallery / Matrix / Table), so switching
// view never loses your filter or selection. Floating, rounded, inset,
// resizable; or embedded (fills the /library page).
// ---------------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { ArrowLeftRight, Box, Braces, Check, ChevronDown, ChevronLeft, ChevronRight, CollectionIcon, Film, Image as ImageIcon, MoreHorizontal, Music, Pencil, Plus, Upload, X } from '@/components/ui/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { InspectorSection } from '@/components/ui/InspectorSection';
import { cn } from '@/lib/utils';
import { emitFocusNode } from '@/lib/focusNodeEvent';
import { trackLibraryPropertyValueSet, trackLibraryViewChanged } from '@/lib/analytics';
import { usePathname } from 'next/navigation';
import { useUIStore } from '@/store/uiStore';
import { useStore as useCanvasStore } from '@/store/yjsStore';
import { getAssetUrl, libraryActions, useLibraryStore, type LibAsset, type ViewDefinition, type ViewLayout, optionIdFor, uid, type LibraryCommand, type Property } from '@/store/libraryStore';
import { FilterControl, PropertyTypeRows, SegTabs, useCollectionPropertyTypesEnabled, type Filters } from './controls';
import { AssetLightbox, GalleryView, MatrixView, TableView, type GalleryCrop, type GallerySize } from './views';
import { usageShots } from './data';
import type { AttrValue } from '@/types/shared';
import { assetMatchesFacet, coerceInput, facetTokens, facetTokenLabel, formatAttrValue, hasAttrValue, isCheckboxLeftover, isFiniteDomain, valuesPresent as tokensPresent } from './propertyValues';
import { idsIntersectingRect, nextAssetSelection } from './assetSelection';
// The Kind facet stores the raw kind token; this maps it to its display label.
const kindLabel = (k: LibAsset['kind'])=>k === 'image' ? 'Image' : k === 'video' ? 'Video' : k === 'audio' ? 'Audio' : k === 'text' ? 'Text' : '3D';
// One quiet word per version-history source (LibAssetVersion.source).
const VERSION_SOURCE_WORD: Record<string, string> = {
    initial: 'Original',
    replace: 'Replaced',
    restore: 'Restored',
    generation: 'Generated',
    upload: 'Uploaded',
    import: 'Imported'
};
const KEY_WIDTH = 'martini-collection-inspector-width';
const KEY_PANEL_W = 'martini-library-asset-panel-w';
// .v2: default flipped to table — the bump re-lands the default over stored prefs.
// Per-collection layout memory. v2 was one GLOBAL key, so a single click of
// "Table" anywhere turned the whole library into spreadsheets forever — the
// gallery-first default only held until the first exploration. v3 scopes the
// choice to the collection where it was made; the stale v2 key is ignored.
const KEY_VIEW = 'martini-collection-view.v3';
const KEY_GSIZE = 'martini-collection-gallery-size';
const KEY_GCROP = 'martini-collection-gallery-crop';
// Ghost tiles for an empty collection: example VARIANT names per category —
// they teach "one subject, many looks" before any asset exists.
// Placeholder tiles hinting at the range of shots a collection can hold.
const GHOSTS = [
    'wide',
    'close-up',
    'detail',
    'alternate look'
];
/** One property row in the asset detail panel — label (opens the property
    menu: rename + type) + a typed value cell. select picks from the vocabulary
    popover; checkbox toggles in place; number/text edit inline. */ function AssetPropertyRow({ prop, value, propertyTypesEnabled, onRename, onSetType, onSetValueByLabel, onSetValue }: {
    prop: Property;
    value: AttrValue | undefined;
    propertyTypesEnabled: boolean;
    onRename: (label: string) => void;
    /** Change the field type in place; stored values are kept (readers tolerate). */ onSetType: (type: Property['type']) => void;
    onSetValueByLabel: (value: string | null) => void;
    onSetValue: (value: AttrValue | null) => void;
}) {
    const t = useTranslations$1();
    const [editingLiteral, setEditingLiteral] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const set = hasAttrValue(value);
    // The property menu: the name edits in place, the type is check-gutter rows.
    const labelCell = (<Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <button title={t("ZmzlGS")} className="flex w-20 shrink-0 items-center gap-1 text-left text-muted-foreground transition-colors hover:text-foreground">
          <span className="min-w-0 truncate">{prop.label}</span>
          <Pencil className="size-2.5 shrink-0 text-muted-foreground/50 opacity-0 transition-opacity group-hover/prop:opacity-100"/>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-52 p-1">
        <input data-quiet-focus defaultValue={prop.label} onKeyDown={(e)=>{
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
    }} onBlur={(e)=>{
        const v = e.target.value.trim();
        if (v && v !== prop.label) onRename(v);
    }} className="mb-1 w-full rounded-md bg-secondary/60 px-2 py-1.5 text-xs text-foreground outline-none"/>
        {propertyTypesEnabled && (<>
            <div className="px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
              {t("-U6ozc")}
            </div>
            <PropertyTypeRows selected={prop.type} onSelect={(type)=>{
        if (type !== prop.type) onSetType(type);
        setMenuOpen(false);
    }}/>
          </>)}
        <div className="mt-1 border-t border-border pt-1">
          <button onClick={()=>{
        libraryActions.deleteProperty(prop.key);
        setMenuOpen(false);
    }} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-destructive hover:bg-destructive/10">
            {t("ArHs9H")}
            <span className="ml-auto shrink-0 text-[10px] text-muted-foreground/60">{t("JZTCK0")}</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>);
    // checkbox — toggle in place. A non-boolean leftover (kept across a type
    // switch) shows as its literal, not a misleading unchecked box; clicking it
    // is an informed replace.
    if (prop.type === 'checkbox') {
        if (isCheckboxLeftover(value)) {
            return (<div className="group/prop flex w-full items-center gap-2 rounded px-1 py-1 text-xs">
          {labelCell}
          <button onClick={()=>onSetValue(true)} title={t("s4lHzQ")} className="min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left text-foreground transition-colors hover:bg-secondary">
            {formatAttrValue(prop, value)}
          </button>
        </div>);
        }
        return (<div className="group/prop flex w-full items-center gap-2 rounded px-1 py-1 text-xs">
        {labelCell}
        <button onClick={()=>onSetValue(value === true ? false : true)} className={cn('flex size-4 items-center justify-center rounded border transition-colors', value === true ? 'border-foreground bg-foreground text-background' : 'border-border text-transparent hover:border-foreground/40')} title={t("LaoRGw")}>
          <Check className="size-3"/>
        </button>
      </div>);
    }
    // number / text — inline edit
    if (prop.type === 'number' || prop.type === 'text') {
        return (<div className="group/prop flex w-full items-center gap-2 rounded px-1 py-1 text-xs">
        {labelCell}
        {editingLiteral ? (<input autoFocus data-quiet-focus // A leftover under a number property rides a text input — a number
        // input would blank the invalid value, and the untouched-editor
        // guard below needs the literal to survive into e.target.value.
        type={prop.type === 'number' && (!set || typeof value === 'number') ? 'number' : 'text'} defaultValue={set ? String(value) : ''} onKeyDown={(e)=>{
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setEditingLiteral(false);
        }} onBlur={(e)=>{
            // Untouched editor commits nothing — coercing an unedited
            // leftover would silently delete it.
            const initial = set ? String(value) : '';
            if (e.target.value !== initial) onSetValue(coerceInput(prop.type, e.target.value));
            setEditingLiteral(false);
        }} className="min-w-0 flex-1 rounded bg-secondary px-1 py-0.5 text-xs text-foreground outline-none"/>) : (<button onClick={()=>setEditingLiteral(true)} className={cn('min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left transition-colors hover:bg-secondary', set ? 'text-foreground' : 'text-muted-foreground/40')}>
            {set ? formatAttrValue(prop, value) : t("-RhnH-")}
          </button>)}
      </div>);
    }
    // select — vocabulary popover
    return (<Popover>
      <div className="group/prop flex w-full items-center gap-2 rounded px-1 py-1 text-xs">
        {labelCell}
        <PopoverTrigger asChild>
          <button className={cn('min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left transition-colors hover:bg-secondary', set ? 'text-foreground' : 'text-muted-foreground/40')}>
            {set ? formatAttrValue(prop, value) : t("-RhnH-")}
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent side="bottom" align="start" className="w-48 p-1">
        <div className="px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
          {prop.label}
        </div>
        {prop.options.map((o)=>(<button key={o.id} onClick={()=>onSetValueByLabel(o.value)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-secondary">
            <Check className={cn('size-3.5 shrink-0', value === o.id ? 'opacity-100' : 'opacity-0')}/>
            <span className="min-w-0 flex-1 truncate text-left">{o.value}</span>
          </button>))}
        <ValueCreateRow onCreate={(v)=>onSetValueByLabel(v)}/>
        {set && (<button onClick={()=>onSetValueByLabel(null)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground">
            <X className="size-3.5 shrink-0"/>
            {t("_GCoTA")}
          </button>)}
      </PopoverContent>
    </Popover>);
}
/** Right-side asset detail — the canvas pattern: click a thing, its facts and
    controls dock on the right. Rename, properties, cover, usage, delete. */ function AssetDetailPanel({ asset, highlighted, isCover, properties, propertyTypesEnabled, width, onWidth, onOpenFull, onRename, onSetValueByLabel, onSetValue, onAddProperty, onAddPropertyWithValue, onUseAsCover, onDelete, onClose, narrow, collectionName, readOnly }: {
    asset: LibAsset;
    highlighted?: boolean;
    isCover: boolean;
    properties: Property[];
    propertyTypesEnabled: boolean;
    width: number;
    onWidth: (w: number) => void;
    onOpenFull: () => void;
    onRename: (name: string) => void;
    onSetValueByLabel: (key: string, value: string | null) => void;
    /** Typed setter (number / checkbox / text). */ onSetValue: (key: string, value: AttrValue | null) => void;
    onAddProperty: (label: string) => string;
    /** One gesture = ONE undo entry: property + option + cell in a single batch. */ onAddPropertyWithValue: (label: string, value: string) => void;
    onUseAsCover: () => void;
    onDelete: () => void;
    onClose: () => void;
    /** Narrow column: a bottom sheet over the collection view (the table stays
      visible above), with a breadcrumb header back to it — not a side peek. */ narrow?: boolean;
    /** Collection name for the narrow breadcrumb ("Cats / Brown cat"). */ collectionName?: string;
    readOnly?: boolean;
}) {
    const t = useTranslations$1();
    const [nameDraft, setNameDraft] = useState(asset.name);
    const [renamingName, setRenamingName] = useState(false);
    // Add-property is one step: a name AND its first value, committed together.
    const [newProp, setNewProp] = useState<{
        name: string;
        value: string;
    } | null>(null);
    const newNameRef = useRef<HTMLInputElement>(null);
    const newValueRef = useRef<HTMLInputElement>(null);
    const newPropDone = useRef(false);
    const replaceRef = useRef<HTMLInputElement>(null);
    // Append-only media history for this asset, newest first.
    const allVersions = useLibraryStore((s)=>s.versions);
    // Attribution: who added this asset (alias resolved from the snapshot's
    // members map; absent for sample/local-only rows).
    const addedBy = useLibraryStore((s)=>(asset.createdBy ? s.members[asset.createdBy] : undefined));
    // Jump back to the canvas node this asset was captured from. Only when we're
    // actually ON that project's canvas can we focus the node in place (there's a
    // canvas mounted to receive the focus); anywhere else — the /library page, or
    // a different project — open that project's canvas in a new tab.
    const pathname = usePathname();
    const inThisCanvas = !!asset.origin?.assetId && !!pathname?.startsWith(`/p/${asset.origin.projectId}`);
    const viewInCanvas = ()=>{
        const o = asset.origin;
        if (!o) return;
        if (inThisCanvas && o.assetId) {
            const cs = useCanvasStore.getState();
            if (o.sceneId) cs.setActiveSceneId(o.sceneId);
            cs.setSelectedAssetId(o.assetId);
            emitFocusNode(`asset-${o.assetId}`);
            onClose(); // collapse the detail so the focused node is visible
        } else {
            // ?shot=<id> is the canvas's shot deep-link: on load it switches to the
            // right canvas, selects the node, and centers the camera on it.
            const url = o.assetId ? `/p/${o.projectId}?shot=${o.assetId}` : `/p/${o.projectId}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };
    const history = useMemo(()=>allVersions.filter((v)=>v.assetId === asset.id).sort((a, b)=>b.versionNumber - a.versionNumber), [
        allVersions,
        asset.id
    ]);
    const openNewProp = ()=>{
        newPropDone.current = false;
        setNewProp({
            name: '',
            value: ''
        });
    };
    const commitNewProp = ()=>{
        if (newPropDone.current) return; // guard the Enter→unmount-blur double-fire
        newPropDone.current = true;
        // Read live DOM values, not state — immune to the batching race between a
        // keystroke and the Enter that commits it.
        const name = newNameRef.current?.value.trim() ?? '';
        const value = newValueRef.current?.value.trim() ?? '';
        if (name) {
            // With a value, the whole gesture is ONE batch (one ⌘Z removes property,
            // option, and cell together — not just the cell).
            if (value) onAddPropertyWithValue(name, value);
            else onAddProperty(name);
        }
        setNewProp(null);
    };
    const cancelNewProp = ()=>{
        newPropDone.current = true;
        setNewProp(null);
    };
    const commitName = ()=>{
        const n = nameDraft.trim();
        if (n && n !== asset.name) onRename(n);
        setRenamingName(false);
    };
    const KindIcon = asset.kind === 'video' ? Film : asset.kind === 'audio' ? Music : asset.kind === 'model3d' ? Box : asset.kind === 'text' ? Braces : ImageIcon;
    return (// The canvas right-panel grammar: an inset floating card with a compact
    // header and collapsible sections (FloatingRightPanelV3). In a narrow
    // column there's no room beside the table — the detail takes over the
    // whole column instead (drill in, back to return).
    <aside style={narrow ? undefined : {
        width
    }} className={cn(highlighted && 'panel-highlight', narrow ? // stacked under the list — that read as two crammed panels). The
    // "‹ Cats" breadcrumb is the way back.
    'absolute inset-0 z-30 flex flex-col overflow-hidden bg-card duration-200 animate-in fade-in slide-in-from-right-2' : 'absolute inset-y-2 end-2 z-30 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl duration-200 animate-in fade-in slide-in-from-right-4')}>
      { /* Left-edge resize — the width is owned by the parent so the collection
          view can reserve exactly this much space (no overlap of the toolbar). */ }
      {!narrow && (<div onMouseDown={(e)=>{
        e.preventDefault();
        const startX = e.clientX;
        const startW = width;
        let latest = width;
        const move = (ev: MouseEvent)=>{
            latest = Math.min(440, Math.max(240, startW + (startX - ev.clientX)));
            onWidth(latest);
        };
        const up = ()=>{
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            if (typeof window !== 'undefined') localStorage.setItem(KEY_PANEL_W, String(latest));
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
    }} className="absolute inset-y-0 -left-1 z-10 w-2 cursor-ew-resize"/>)}

      { /* Header — icon + name (inline rename) + close, matching the canvas. */ }
      <div className="flex h-9 shrink-0 items-center gap-1.5 border-b border-border px-2.5 select-none">
        {narrow && (/* Breadcrumb — "‹ Cats / <asset>": the drill-in's way back up. */ <>
            <button onClick={onClose} title={t("FdnBgG")} className="-ms-1 flex max-w-32 shrink-0 items-center gap-0.5 truncate rounded py-0.5 pe-1 ps-0.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground">
              <ChevronLeft className="size-3.5 shrink-0"/>
              <span className="truncate">{collectionName || t("FdnBgG")}</span>
            </button>
            { /* eslint-disable-next-line i18next/no-literal-string -- breadcrumb separator */ }
            <span className="shrink-0 text-xs text-muted-foreground/40">/</span>
          </>)}
        <KindIcon className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75}/>
        {renamingName && !readOnly ? (<input autoFocus data-quiet-focus value={nameDraft} onChange={(e)=>setNameDraft(e.target.value)} onBlur={commitName} onKeyDown={(e)=>{
        if (e.key === 'Enter') commitName();
        if (e.key === 'Escape') {
            setNameDraft(asset.name);
            setRenamingName(false);
        }
    }} className="min-w-0 flex-1 rounded bg-secondary px-1 py-0.5 text-xs font-medium text-foreground outline-none"/>) : readOnly ? (<span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{asset.name}</span>) : (<button onClick={()=>{
        setNameDraft(asset.name);
        setRenamingName(true);
    }} title={t("iXNbPf")} className="group/n flex min-w-0 flex-1 items-center gap-1 truncate text-xs font-medium text-foreground">
            <span className="truncate">{asset.name}</span>
            <Pencil className="size-3 shrink-0 text-transparent group-hover/n:text-muted-foreground"/>
          </button>)}
        {!readOnly && (<Popover>
            <PopoverTrigger asChild>
              <button title={t("S8_4ZI")} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground">
                <MoreHorizontal className="size-3.5" strokeWidth={1.75}/>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" side="bottom" className="w-44 p-1">
              {asset.kind !== 'text' && (<button onClick={()=>replaceRef.current?.click()} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-secondary">
                  {t("MU7AiV")}
                </button>)}
              <button onClick={onUseAsCover} disabled={isCover} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-secondary disabled:cursor-default disabled:text-muted-foreground/40">
                {isCover ? t("TilRBW") : t("3KTdmb")}
              </button>
              <div className="my-1 border-t border-border"/>
              <button onClick={onDelete} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-destructive/90 hover:bg-destructive/10 hover:text-destructive">
                {t("2gva1W")}
              </button>
            </PopoverContent>
          </Popover>)}
        {!narrow && (<button onClick={onClose} title={t("fhfSHc")} className="-me-0.5 shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground">
            <X className="size-3.5" strokeWidth={1.75}/>
          </button>)}
      </div>

      { /* Body — one natural scroll flow (thumbnail → sections → actions), the
          way the canvas SubjectDetailPanel reads. No pinned footer, so short
          content never leaves a dead void in the middle. */ }
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-5 p-4">
          {asset.kind === 'text' ? (// Text fragments have no media — the "thumbnail" is the editable body.
    <textarea key={asset.id} defaultValue={asset.textValue ?? ''} readOnly={readOnly} onBlur={(e)=>{
        if (!readOnly) libraryActions.updateTextFragment(asset.id, e.target.value);
    }} placeholder={t("kV2ggJ")} className="min-h-32 w-full resize-y rounded-md border border-border bg-secondary/40 p-2.5 text-xs leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/40"/>) : (<button onClick={onOpenFull} title={t("Gh33Fn")} className="block w-full overflow-hidden rounded-md border border-border bg-secondary/20 transition-opacity hover:opacity-90">
              { /* Original aspect ratio — the detail panel shows the asset, never a crop of it. */ }
              <img src={getAssetUrl(asset, 'large') || getAssetUrl(asset)} alt="" className="max-h-64 w-full object-contain"/>
            </button>)}

          <InspectorSection sectionKey="lib-asset-properties" label={t("aI80kg")}>
            <div className="space-y-0.5">
              {properties.map((prop)=>readOnly ? (<div key={prop.key} className="flex items-center gap-2 px-1 py-1 text-xs">
                    <span className="w-20 shrink-0 truncate text-muted-foreground">{prop.label}</span>
                    <span className="min-w-0 flex-1 truncate text-foreground/80">
                      {formatAttrValue(prop, asset.attrs[prop.key]) || t("SL-c5a")}
                    </span>
                  </div>) : (<AssetPropertyRow key={prop.key} prop={prop} value={asset.attrs[prop.key]} propertyTypesEnabled={propertyTypesEnabled} onRename={(label)=>libraryActions.renameProperty(prop.key, label)} onSetType={(type)=>libraryActions.setPropertyType(prop.key, type)} onSetValueByLabel={(v)=>onSetValueByLabel(prop.key, v)} onSetValue={(v)=>onSetValue(prop.key, v)}/>))}
              {!readOnly && (newProp !== null ? (// One row, two fields — name then value — mirroring a property
    // row's label|value layout. Enter on name advances to value;
    // Enter on value creates the property already filled in.
    <div className="flex items-center gap-2 px-1 py-1" onBlur={(e)=>{
        // Commit only when focus leaves the row (not name→value tab).
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        commitNewProp();
    }}>
                    <input ref={newNameRef} autoFocus data-quiet-focus value={newProp.name} onChange={(e)=>setNewProp((p)=>(p ? {
                ...p,
                name: e.target.value
            } : p))} onKeyDown={(e)=>{
        if (e.key === 'Enter') {
            e.preventDefault();
            newValueRef.current?.focus();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            cancelNewProp();
        }
    }} placeholder={t("-SDMR3")} className="w-20 shrink-0 rounded bg-secondary px-2 py-1 text-xs text-foreground outline-none placeholder:text-muted-foreground/50"/>
                    <input ref={newValueRef} value={newProp.value} onChange={(e)=>setNewProp((p)=>(p ? {
                ...p,
                value: e.target.value
            } : p))} onKeyDown={(e)=>{
        if (e.key === 'Enter') {
            e.preventDefault();
            commitNewProp();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            cancelNewProp();
        }
    }} placeholder={t("GufXy5")} className="min-w-0 flex-1 rounded bg-secondary px-2 py-1 text-xs text-foreground outline-none placeholder:text-muted-foreground/50"/>
                  </div>) : (<button onClick={openNewProp} className="flex items-center gap-1 rounded px-1 py-1 text-[11px] text-muted-foreground/60 transition-colors hover:bg-secondary hover:text-foreground">
                    <Plus className="size-3"/> {t("_afZqm")}
                  </button>))}
            </div>
          </InspectorSection>

          <InspectorSection sectionKey="lib-asset-info" label={t("we4Lby")}>
            <dl className="space-y-1 text-[11px]">
              <InfoRow label={t("e5x8FT")}>{kindLabel(asset.kind)}</InfoRow>
              <InfoRow label={t("aH4De2")}>
                {asset.sourceKind === 'upload' ? t("LwdDVa") : asset.sourceKind === 'generation' ? t("mjbpwF") : asset.sourceKind === 'import' || !asset.src.startsWith('idb:') // i18n-ignore -- URL-scheme token
     ? t("i3mDUz") : t("rIAJlE")}
                { /* eslint-disable-next-line i18next/no-literal-string -- punctuation separator */ }
                {' · '}
                {new Date(asset.createdAt).toLocaleDateString()}
              </InfoRow>
              {asset.originalFilename && <InfoRow label={t("gyrIEl")}>{asset.originalFilename}</InfoRow>}
              {asset.width && asset.height && (<InfoRow label={t("agOXPD")}>
                  { /* eslint-disable-next-line i18next/no-literal-string -- dimension separator glyph */ }
                  {asset.width} × {asset.height}
                  {asset.sizeBytes ? ` · ${formatBytes(asset.sizeBytes)}` : ''}
                </InfoRow>)}
              <InfoRow label={t("rIAJlE")}>
                {timeAgo(asset.createdAt)}
                { /* eslint-disable-next-line i18next/no-literal-string -- punctuation separator */ }
                {addedBy ? ` · ${addedBy}` : ''}
              </InfoRow>
              {asset.origin && (// Provenance: captured from a project canvas — jump back to the
    // exact node (same project focuses it in place).
    <InfoRow label="Origin">
                  <button onClick={viewInCanvas} className="text-foreground underline-offset-2 hover:underline">
                    {inThisCanvas ? 'View in canvas' : 'Open in canvas ↗'}
                  </button>
                </InfoRow>)}
            </dl>
          </InspectorSection>

          {history.length >= 2 && (<InspectorSection sectionKey="lib-asset-history" label={t("djJp6c")}>
              <div className="space-y-0.5">
                {history.map((v)=>{
        const isCurrent = v.id === asset.currentVersionId;
        const src = v.posterSrc ?? v.src;
        const thumb = src.startsWith('https') ? src : null;
        return (<div key={v.id} className="group/hist flex items-center gap-2 rounded px-1 py-1 text-[11px]">
                      <div className="size-8 shrink-0 overflow-hidden rounded border border-border bg-secondary/40">
                        {thumb && <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover"/>}
                      </div>
                      <div className="min-w-0 flex-1 leading-tight">
                        <div className="flex items-center gap-1.5">
                          { /* eslint-disable-next-line i18next/no-literal-string -- version token */ }
                          <span className="font-medium text-foreground">v{v.versionNumber}</span>
                          <span className="truncate text-muted-foreground">
                            {VERSION_SOURCE_WORD[v.source] ?? 'Original'}
                          </span>
                        </div>
                        <div className="text-muted-foreground/60">{timeAgo(v.createdAt)}</div>
                      </div>
                      {isCurrent ? (<span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground/50">
                          Current
                        </span>) : !readOnly ? (<button onClick={()=>libraryActions.setAssetCurrentVersion(asset.id, v.id)} className="shrink-0 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground group-hover/hist:opacity-100">
                          Restore
                        </button>) : null}
                    </div>);
    })}
              </div>
            </InspectorSection>)}

          <InspectorSection sectionKey="lib-asset-usage" label={t("wbsq7O")}>
            <p className="text-[11px] leading-relaxed text-muted-foreground/60">{t("tbOtMd")}</p>
          </InspectorSection>
        </div>
      </div>

      { /* Replace-media file input lives at the panel root (triggered from the
          header overflow menu). */ }
      {!readOnly && (<input ref={replaceRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e)=>{
        const f = e.target.files?.[0];
        e.target.value = '';
        if (f) void libraryActions.replaceAssetMedia(asset.id, f);
    }}/>)}
    </aside>);
}
function InfoRow({ label, children }: {
    label: string;
    children: React.ReactNode;
}) {
    return (<div className="flex gap-2">
      <dt className="w-14 shrink-0 text-muted-foreground/60">{label}</dt>
      <dd className="min-w-0 flex-1 truncate text-muted-foreground" title={typeof children === 'string' ? children : undefined}>
        {children}
      </dd>
    </div>);
}
function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function timeAgo(ms: number): string {
    const s = Math.floor((Date.now() - ms) / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}
/** Type-to-create row inside a property value popover. */ function ValueCreateRow({ onCreate, placeholder }: {
    onCreate: (value: string) => void;
    placeholder?: string;
}) {
    const t = useTranslations$1();
    const [draft, setDraft] = useState('');
    return (<input value={draft} data-quiet-focus onChange={(e)=>setDraft(e.target.value)} onKeyDown={(e)=>{
        if (e.key === 'Enter' && draft.trim()) {
            onCreate(draft.trim());
            setDraft('');
        }
    }} placeholder={placeholder ?? t("Hf3q3k")} className="mt-0.5 w-full rounded-md bg-secondary/60 px-2 py-1.5 text-xs text-foreground outline-none placeholder:text-muted-foreground/50"/>);
}
const MIN_W = 320;
const MAX_W = 760;
const ghost = 'rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground';
function useLocal<T extends string>(key: string, fallback: T): [T, (v: T) => void] {
    const [v, setV] = useState<T>(()=>{
        if (typeof window === 'undefined') return fallback;
        return (localStorage.getItem(key) as T) || fallback;
    });
    const set = (next: T)=>{
        setV(next);
        if (typeof window !== 'undefined') localStorage.setItem(key, next);
    };
    return [
        v,
        set
    ];
}
/** A quiet inline property picker used for group-by and matrix axes — the
 * app's popover-row grammar (ghost trigger, check-gutter rows), never a
 * native <select>. */ function PropPicker({ value, options, exclude, label, onChange, allowNone, prefix, menuLabel }: {
    value: string;
    options: string[];
    exclude?: string;
    label: (key: string) => string;
    onChange: (v: string) => void;
    allowNone?: boolean;
    /** Function label before the value ('Group') so the control names itself. */ prefix?: string;
    /** Heading inside the menu ('Group by') so the list names its function. */ menuLabel?: string;
}) {
    const t = useTranslations$1();
    const [open, setOpen] = useState(false);
    const rows = [
        ...(allowNone ? [
            ''
        ] : []),
        ...options.filter((f)=>f === value || f !== exclude)
    ];
    return (<Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex h-7 items-center gap-1.5 rounded-md px-2 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
          {prefix && <span className="text-muted-foreground/60">{prefix}</span>}
          <span className={cn(value && 'text-foreground')}>{value ? label(value) : t("450Fty")}</span>
          <ChevronDown className="size-3"/>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-44 p-1">
        {menuLabel && (<div className="px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50">
            {menuLabel}
          </div>)}
        {rows.map((f)=>(<button key={f || '~none'} onClick={()=>{
            onChange(f);
            setOpen(false);
        }} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-secondary">
            <Check className={cn('size-3.5 shrink-0', f === value ? 'opacity-100' : 'opacity-0')}/>
            <span className="flex-1 truncate text-left">{f ? label(f) : t("450Fty")}</span>
          </button>))}
      </PopoverContent>
    </Popover>);
}
export default function CollectionInspector({ id, onClose, embedded, narrow, initialAssetId, initialAssetRevealNonce, surface, readOnly }: {
    id: string;
    onClose: () => void;
    /** Embedded = fills its parent (the /library page); default floats over the canvas. */ embedded?: boolean;
    /** Which host renders this inspector, for analytics — `embedded` can't tell
   * (the in-editor panel embeds it too). */ surface: 'page' | 'panel';
    /** Narrow column (the in-editor panel): asset detail DRILLS IN — it replaces
   * the collection view with a back header instead of opening a side peek,
   * which has no room to sit beside the table at ~420px. */ narrow?: boolean;
    /** Open with this asset's detail already showing (tile click = look). */ initialAssetId?: string;
    /** Replays arrival feedback when an outside mention reveals this asset. */ initialAssetRevealNonce?: number;
    /** Project guests may inspect and use enabled assets, but cannot mutate the owning Library vault. */ readOnly?: boolean;
}) {
    const t = useTranslations$1();
    const c = useLibraryStore((s)=>s.collections.find((x)=>x.id === id));
    const tagVocab = useLibraryStore((st)=>st.tagVocab);
    const allAssets = useLibraryStore((s)=>s.assets);
    const properties = useLibraryStore((s)=>s.properties);
    const collections = useLibraryStore((s)=>s.collections);
    const importFiles = useLibraryStore((s)=>s.importFiles);
    const canvasBottomInset = useUIStore((s)=>s.canvasBottomInset);
    const { renameCollection, setCollectionDescription, moveAssets, deleteAssets, setHero, setCollectionTags, addProperty } = libraryActions;
    // setAssetValueByLabel relies on `this` — call it through the façade, never destructured.
    const setAssetValueByLabel = (aid: string, key: string, label: string | null)=>libraryActions.setAssetValueByLabel(aid, key, label);
    const assets = useMemo(()=>allAssets.filter((a)=>a.collectionId === id && !a.deletedAt), [
        allAssets,
        id
    ]);
    // Vocabulary is the workspace's live property set; a collection's columns/axes
    // narrow to the ones actually used on its assets (the discriminating rule below).
    const liveProps = useMemo(()=>properties.filter((p)=>!p.deletedAt), [
        properties
    ]);
    const propKeys = useMemo(()=>liveProps.map((p)=>p.key), [
        liveProps
    ]);
    const [descEditing, setDescEditing] = useState(false);
    // The narrow embedded column and the wide /library page want different
    // defaults AND different remembered preferences — separate keys.
    const [gallerySize, setGallerySize] = useLocal<GallerySize>(embedded ? `${KEY_GSIZE}:embedded` : KEY_GSIZE, embedded ? 'Small' : 'Medium');
    const [galleryCrop, setGalleryCrop] = useLocal<GalleryCrop>(embedded ? `${KEY_GCROP}:embedded` : KEY_GCROP, embedded ? 'Square' : 'Original');
    // One ViewDefinition is the single source of truth for layout + filters + groupBy
    // + axes (spec §3/§4). Layout persists per-user via localStorage; filters/groupBy/
    // axes are per-collection and reset with the keyed remount (page keys by id).
    const [definition, setDefinition] = useState<ViewDefinition>(()=>{
        // Gallery first — a collection is imagery; a spreadsheet of "Saved asset"
        // rows is the wrong first impression for a visual library.
        const layout = ((typeof window !== 'undefined' && localStorage.getItem(`${KEY_VIEW}.${id}`)) as ViewLayout | null) || 'gallery';
        return {
            collectionId: id,
            layout
        };
    });
    const [selection, setSelection] = useState<Set<string>>(new Set());
    const selectionAnchor = useRef<string | null>(null);
    // Marquee select (gallery): hooks live above the `!c` early return; the
    // handlers that use them sit with the other actions further down.
    const marqueeHost = useRef<HTMLDivElement>(null);
    const [marqueeBox, setMarqueeBox] = useState<{
        left: number;
        top: number;
        width: number;
        height: number;
    } | null>(null);
    const marqueeDrag = useRef<{
        startX: number;
        startY: number;
        base: Set<string>;
        active: boolean;
    } | null>(null);
    const [showAllCols, setShowAllCols] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [tagAdding, setTagAdding] = useState(false);
    const [tagDraft, setTagDraft] = useState('');
    const [tagIdx, setTagIdx] = useState(0);
    // Asset context menu — actions live on the thing (right-click), not a dock.
    const [assetCtx, setAssetCtx] = useState<{
        x: number;
        y: number;
        id: string;
    } | null>(null);
    // Lightbox: click = look at it; arrows step through the filtered order.
    const [lightbox, setLightbox] = useState<string | null>(null);
    // Click = inspect (right panel, the canvas pattern); double-click = lightbox.
    const [detailId, setDetailId] = useState<string | null>(initialAssetId ?? null);
    // Peek width lives here so the collection view can reserve exactly this much
    // space when the peek is open — the panel never overlaps the view controls.
    const [peekWidth, setPeekWidth] = useState(()=>{
        if (typeof window === 'undefined') return 288;
        const stored = Number(localStorage.getItem(KEY_PANEL_W));
        return stored >= 240 && stored <= 440 ? stored : 288;
    });
    const [ctxView, setCtxView] = useState<'root' | 'props' | 'move' | {
        prop: string;
    }>('root');
    const [dragOver, setDragOver] = useState(false);
    const propertyTypesEnabled = useCollectionPropertyTypesEnabled();
    const [width, setWidth] = useState(()=>{
        if (typeof window === 'undefined') return 380;
        const s = localStorage.getItem(KEY_WIDTH);
        return s ? Math.min(MAX_W, Math.max(MIN_W, Number(s))) : 380;
    });
    const fileRef = useRef<HTMLInputElement>(null);
    useEffect(()=>{
        const onKey = (e: KeyboardEvent)=>{
            if (e.key !== 'Escape') return;
            // A focused text field owns its own Escape (rename, cell/property editor).
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            // An open popover owns Escape (Radix listens on bubble; we're on capture).
            if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
            e.stopPropagation();
            if (lightbox) setLightbox(null);
            else if (assetCtx) setAssetCtx(null);
            else if (detailId) setDetailId(null);
            else if (selection.size) setSelection(new Set());
            else onClose();
        };
        window.addEventListener('keydown', onKey, true);
        return ()=>window.removeEventListener('keydown', onKey, true);
    }, [
        onClose,
        selection.size,
        assetCtx,
        lightbox,
        detailId
    ]);
    // Left-edge resize.
    const resizing = useRef<{
        x: number;
        w: number;
    } | null>(null);
    useEffect(()=>{
        const move = (e: MouseEvent)=>{
            if (!resizing.current) return;
            setWidth(Math.min(MAX_W, Math.max(MIN_W, resizing.current.w + (resizing.current.x - e.clientX))));
        };
        const up = ()=>{
            if (resizing.current && typeof window !== 'undefined') localStorage.setItem(KEY_WIDTH, String(width));
            resizing.current = null;
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        return ()=>{
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
        };
    }, [
        width
    ]);
    if (!c) return null;
    // --- The current view, unpacked from the one definition -------------------
    const view = definition.layout;
    const filters: Filters = definition.filters ?? {};
    const groupBy = definition.groupBy ?? '';
    const axes: [string, string] = definition.axes ? [
        definition.axes.x,
        definition.axes.y
    ] : [
        '',
        ''
    ];
    const sort = definition.sort ?? {
        by: 'name',
        dir: 'asc' as const
    };
    const patchDef = (patch: Partial<ViewDefinition>)=>setDefinition((d)=>({
                ...d,
                ...patch
            }));
    const setView = (layout: ViewLayout)=>{
        patchDef({
            layout
        });
        if (typeof window !== 'undefined') localStorage.setItem(`${KEY_VIEW}.${id}`, layout);
        trackLibraryViewChanged({
            workspaceId: useLibraryStore.getState().workspaceId,
            layout,
            surface
        });
    };
    const setFilters = (f: Filters)=>patchDef({
            filters: Object.keys(f).length ? f : undefined
        });
    const setGroupBy = (v: string)=>patchDef({
            groupBy: v || undefined
        });
    const setAxes = (t: [string, string])=>patchDef({
            axes: {
                x: t[0],
                y: t[1]
            }
        });
    const setSort = (by: string)=>patchDef({
            sort: sort.by === by ? {
                by,
                dir: sort.dir === 'asc' ? 'desc' : 'asc'
            } : {
                by,
                dir: 'asc'
            }
        });
    // --- Vocabulary accessors (workspace property set) ------------------------
    const propByKey = (key: string): Property | undefined =>liveProps.find((p)=>p.key === key);
    const propLabel = (key: string)=>propByKey(key)?.label ?? key;
    const propOptions = (key: string)=>propByKey(key)?.options ?? [];
    // Typed display: select→label, number→formatted, checkbox→Yes/No, text→literal.
    // Select still resolves option IDS via the vocabulary (spec §4).
    const attrLabel = (key: string, value: AttrValue | undefined)=>formatAttrValue(propByKey(key), value);
    // Distinct facet tokens present on these assets, in display order (spec 49).
    const valuesPresent = (list: LibAsset[], key: string): string[] =>{
        const prop = propByKey(key);
        if (!prop) return [];
        return tokensPresent(prop, list.map((a)=>a.attrs[key]));
    };
    // Filters store rename-proof tokens per facet (raw kind for `kind`, option ids
    // for select, literal tokens for text/number, true/false for checkbox) —
    // so a renamed option never orphans a live filter or a saved view.
    const filterAssets = (list: LibAsset[], f: Filters): LibAsset[] =>list.filter((a)=>{
            for (const [key, vals] of Object.entries(f)){
                if (!vals.length) continue;
                if (key === 'kind') {
                    if (!vals.includes(a.kind)) return false;
                } else {
                    const prop = propByKey(key);
                    if (prop && !assetMatchesFacet(prop, a.attrs[key], vals)) return false;
                }
            }
            return true;
        });
    // --- Discriminating properties (the declutter rule) -----------------------
    // A property earns a column/axis only if ≥2 distinct values exist here.
    const discriminating = propKeys.filter((k)=>valuesPresent(assets, k).length >= 2);
    // Properties are workspace-wide, but a collection shouldn't inherit every
    // property some other collection uses — the asset detail lists only ones
    // valued somewhere in THIS collection; "+ property" adds (or, by matching
    // label, re-binds) the rest.
    const usedProps = liveProps.filter((p)=>valuesPresent(assets, p.key).length > 0);
    const orderedProps = [
        ...discriminating,
        ...propKeys.filter((k)=>!discriminating.includes(k))
    ];
    const hiddenCount = orderedProps.length - discriminating.length;
    const columns = showAllCols ? orderedProps : discriminating;
    // Group-by / axes are categorical: only finite-domain types (select,
    // checkbox) — grouping by free text or a number isn't meaningful.
    const groupableProps = orderedProps.filter((k)=>isFiniteDomain(propByKey(k)?.type ?? 'select'));
    const matrixAvailable = discriminating.filter((k)=>isFiniteDomain(propByKey(k)?.type ?? 'select')).length >= 2;
    // v1: Matrix is cut from the view switcher. Views are serializable data, so
    // this is reversible with no migration — flip when typed axes are rich enough
    // and matrix gap-fill (its actual payoff) lands. Code below stays intact.
    const SHOW_MATRIX = false;
    const effectiveView = view === 'matrix' && (!matrixAvailable || !SHOW_MATRIX) ? 'gallery' : view;
    const rowKey = axes[0] || discriminating[0] || '';
    const colKey = axes[1] || discriminating[1] || '';
    const filtered = filterAssets(assets, filters);
    // --- Filter facet defs + live counts --------------------------------------
    // Facet values are tokens (raw kind / option id); FilterControl resolves labels
    // via labelFor below, so filters survive option renames.
    const kinds = new Set<string>(assets.map((a)=>a.kind));
    const facetDefs = [
        ...(kinds.size >= 2 ? [
            {
                key: 'kind',
                label: 'Kind',
                values: ([
                    'image',
                    'video',
                    'audio',
                    'model3d'
                ] as string[]).filter((k)=>kinds.has(k))
            }
        ] : []),
        ...orderedProps.map((k)=>({
                key: k,
                label: propLabel(k),
                values: valuesPresent(assets, k)
            }))
    ];
    const facetLabel = (facet: string, value: string)=>{
        if (facet === 'kind') return kindLabel(value as LibAsset['kind']);
        const prop = propByKey(facet);
        return prop ? facetTokenLabel(prop, value) : value;
    };
    // Counts are per-TOKEN (option id / literal / true-false).
    const counts = (facet: string)=>{
        const others = {
            ...filters
        };
        delete others[facet];
        const base = filterAssets(assets, others);
        const prop = facet === 'kind' ? undefined : propByKey(facet);
        // Null-prototype: tokens are user text ('__proto__', 'constructor' would
        // hit inherited members on a plain object and miscount or crash render).
        const out: Record<string, number> = Object.create(null);
        base.forEach((a)=>{
            const tokens = facet === 'kind' ? (a.kind ? [
                a.kind
            ] : []) : prop ? facetTokens(prop, a.attrs[facet]) : [];
            for (const tk of tokens)out[tk] = (out[tk] ?? 0) + 1;
        });
        return out;
    };
    // --- Actions --------------------------------------------------------------
    const clearSelection = ()=>{
        setSelection(new Set());
        selectionAnchor.current = null;
    };
    // --- Marquee select (gallery only) ---------------------------------------
    // Drag on empty background draws a rectangle; tiles keep their own drag.
    // Coordinates are CONTENT-space (client + scroll), so the overlay — an
    // absolute child of the scrolling container — stays glued through scrolls.
    const contentPoint = (e: MouseEvent | React.MouseEvent)=>{
        const host = marqueeHost.current!;
        const r = host.getBoundingClientRect();
        return {
            x: e.clientX - r.left + host.scrollLeft,
            y: e.clientY - r.top + host.scrollTop
        };
    };
    const onMarqueeMouseDown = (e: React.MouseEvent)=>{
        const host = marqueeHost.current;
        if (effectiveView !== 'gallery' || e.button !== 0 || !host) return;
        // Tiles, controls, and menus own their own mousedown — marquee starts on background only.
        if ((e.target as Element).closest('[data-asset-id], button, a, input, textarea, [role="menu"]')) return;
        // Native scrollbar hits the host beyond its client box — never marquee from it.
        const hostRect = host.getBoundingClientRect();
        if (e.clientX - hostRect.left > host.clientWidth || e.clientY - hostRect.top > host.clientHeight) return;
        // No preventDefault: the mousedown must still blur an open caption/rename
        // editor (commit-on-blur). Text selection is suppressed on activation.
        const { x, y } = contentPoint(e);
        const additive = e.metaKey || e.ctrlKey || e.shiftKey;
        marqueeDrag.current = {
            startX: x,
            startY: y,
            base: additive ? new Set(selection) : new Set(),
            active: false
        };
        const onMove = (ev: MouseEvent)=>{
            const drag = marqueeDrag.current;
            const host = marqueeHost.current;
            if (!drag || !host) return;
            const p = contentPoint(ev);
            // 4px threshold: below it this is a click, and background-click clears.
            if (!drag.active && Math.abs(p.x - drag.startX) < 4 && Math.abs(p.y - drag.startY) < 4) return;
            if (!drag.active) window.getSelection()?.removeAllRanges();
            drag.active = true;
            const rect = {
                left: Math.min(drag.startX, p.x),
                top: Math.min(drag.startY, p.y),
                right: Math.max(drag.startX, p.x),
                bottom: Math.max(drag.startY, p.y)
            };
            setMarqueeBox({
                left: rect.left,
                top: rect.top,
                width: rect.right - rect.left,
                height: rect.bottom - rect.top
            });
            const hostRect = host.getBoundingClientRect();
            const tiles = [
                ...host.querySelectorAll<HTMLElement>('[data-asset-id]')
            ].map((el)=>{
                const r = el.getBoundingClientRect();
                return {
                    id: el.dataset.assetId!,
                    left: r.left - hostRect.left + host.scrollLeft,
                    top: r.top - hostRect.top + host.scrollTop,
                    right: r.right - hostRect.left + host.scrollLeft,
                    bottom: r.bottom - hostRect.top + host.scrollTop
                };
            });
            setSelection(new Set([
                ...drag.base,
                ...idsIntersectingRect(rect, tiles)
            ]));
        };
        const onUp = ()=>{
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            const wasActive = marqueeDrag.current?.active;
            marqueeDrag.current = null;
            setMarqueeBox(null);
            if (!wasActive) clearSelection();
            else selectionAnchor.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    /** Right-click acts on the selection when the target is in it, else just the target. */ const ctxIds = assetCtx ? (selection.has(assetCtx.id) ? [
        ...selection
    ] : [
        assetCtx.id
    ]) : [];
    const closeCtx = ()=>{
        setAssetCtx(null);
        setCtxView('root');
    };
    const openAssetCtx = (e: React.MouseEvent, aid: string)=>{
        e.preventDefault();
        e.stopPropagation();
        if (readOnly) return;
        setCtxView('root');
        setAssetCtx({
            x: e.clientX,
            y: e.clientY,
            id: aid
        });
    };
    const toggleSel = (assetId: string, event: React.MouseEvent)=>{
        // Range only where visual order == `filtered` order: the table re-sorts
        // internally and group bands reorder, so a range there would span a
        // store-order run the user can't see. Shift degrades to toggle instead.
        const rangeOk = effectiveView === 'gallery' && !groupBy;
        const range = event.shiftKey && rangeOk;
        setSelection((prev)=>{
            const next = nextAssetSelection({
                selection: prev,
                anchorId: selectionAnchor.current,
                clickedId: assetId,
                orderedIds: filtered.map((asset)=>asset.id),
                additive: event.metaKey || event.ctrlKey || !range,
                range
            });
            selectionAnchor.current = next.anchorId;
            return next.selection;
        });
    };
    // Text fragments have no pixels — never open the full-bleed lightbox on one.
    const openLightbox = (assetId: string)=>{
        if (assets.find((a)=>a.id === assetId)?.kind !== 'text') setLightbox(assetId);
    };
    const applyPropTo = (targets: string[], key: string, value: string | null)=>{
        if (readOnly) return;
        // One gesture = ONE undo entry (spec §5.4): resolve/create the option
        // once, then set every asset in a single batch command.
        const state = useLibraryStore.getState();
        const commands: LibraryCommand[] = [];
        let optionId: string | null = null;
        if (value !== null) {
            optionId = optionIdFor(state.properties, key, value) ?? null;
            if (!optionId) {
                const option = {
                    id: uid(),
                    value: value.trim()
                };
                commands.push({
                    type: 'property.addOption',
                    key,
                    option
                });
                optionId = option.id;
            }
        }
        commands.push(...targets.map((id)=>({
                type: 'asset.setValue',
                id,
                key,
                value: optionId
            }) as LibraryCommand));
        state.dispatch({
            type: 'batch',
            label: t("rf29dP"),
            commands
        }, {
            label: t("rf29dP")
        });
        trackLibraryPropertyValueSet({
            workspaceId: state.workspaceId,
            propertyType: state.properties.find((p)=>p.key === key && !p.deletedAt)?.type,
            cleared: value === null,
            count: targets.length
        });
    };
    const ctxMove = (targetId: string)=>{
        if (readOnly) return;
        moveAssets(ctxIds, targetId);
        clearSelection();
        closeCtx();
    };
    const ctxRemove = ()=>{
        if (readOnly) return;
        deleteAssets(ctxIds);
        clearSelection();
        closeCtx();
    };
    const setAttr = (assetId: string, key: string, value: string | null)=>{
        if (readOnly) return;
        // Label-based: resolves to an option id, creating the option when new.
        setAssetValueByLabel(assetId, key, value);
    };
    // Typed-value setter (number, boolean, text) — bypasses the
    // option-vocabulary path that setAttr uses for select.
    const setAttrRaw = (assetId: string, key: string, value: AttrValue | null)=>{
        if (readOnly) return;
        libraryActions.setAssetValue(assetId, key, value);
    };
    const onAddProperty = (label: string, type: Property['type'] = propertyTypesEnabled ? 'select' : 'text'): string =>{
        if (readOnly) return '';
        const key = addProperty(label, propertyTypesEnabled ? type : 'text');
        setShowAllCols(true); // a fresh property is non-discriminating; reveal it so it can be filled
        return key // caller sets the first value on it in the same step
        ;
    };
    const pickFiles = ()=>{
        if (!readOnly) fileRef.current?.click();
    };
    const onFiles = (files: FileList | null)=>{
        if (readOnly) return;
        if (files && files.length) void importFiles([
            ...files
        ], id);
    };
    const commitRename = ()=>{
        if (readOnly) return;
        const n = nameDraft.trim();
        if (n && n !== c.name) renameCollection(id, n);
        setRenaming(false);
    };
    // Tag editor: suggestions from the vocabulary + a Create row for new words.
    // Enter/click commit; blur cancels (typos shouldn't become vocabulary).
    const tagDraftNorm = tagDraft.trim().replace(/^#/, '');
    const tagSuggestions = tagAdding ? tagVocab.filter((t)=>!c.tags.includes(t) && t.toLowerCase().includes(tagDraftNorm.toLowerCase())).slice(0, 6) : [];
    const tagCreatable = !!tagDraftNorm && !c.tags.some((t)=>t.toLowerCase() === tagDraftNorm.toLowerCase()) && !tagSuggestions.some((t)=>t.toLowerCase() === tagDraftNorm.toLowerCase());
    const tagOptionCount = tagSuggestions.length + (tagCreatable ? 1 : 0);
    const tagActiveIdx = Math.min(tagIdx, Math.max(0, tagOptionCount - 1));
    const pickTag = (t: string)=>{
        if (readOnly) return;
        const clean = t.trim().replace(/^#/, '');
        if (clean && !c.tags.includes(clean)) setCollectionTags(id, [
            ...c.tags,
            clean
        ]);
        setTagDraft('');
        setTagIdx(0);
        setTagAdding(false);
    };
    const effWidth = narrow ? 420 : embedded ? 1200 : width;
    const showUsage = c.usedIn > 0;
    // Demo shot rows exist only for the sample catalog — usageShots() fabricates
    // them by name. Real collections get the count alone until library_links'
    // project/generation ids are wired into a real shot list.
    const shots = showUsage && c.sample ? usageShots(c.name) : [];
    const otherCollections = collections.filter((x)=>x.id !== id && !x.deletedAt);
    const sliceKey = discriminating[0];
    const sliceId = sliceKey ? assets.find((a)=>a.attrs[sliceKey])?.attrs[sliceKey] : undefined;
    const sliceVal = sliceId ? attrLabel(sliceKey, sliceId).toLowerCase() : '';
    return (<div style={embedded ? {
        paddingRight: detailId && !narrow ? peekWidth + 16 : undefined
    } : {
        width,
        bottom: canvasBottomInset + 16
    }} className={embedded ? 'relative flex h-full w-full flex-col overflow-hidden transition-[padding] duration-200' : 'fixed end-4 top-14 z-40 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl duration-200 animate-in fade-in slide-in-from-right-2'}>
      {!embedded && (<div onMouseDown={(e)=>{
        resizing.current = {
            x: e.clientX,
            w: width
        };
        e.preventDefault();
    }} className="group/resize absolute inset-y-0 -left-1 z-10 w-2 cursor-ew-resize">
          <div className="absolute inset-y-0 left-1 w-px bg-transparent transition-colors group-hover/resize:bg-border"/>
        </div>)}

      { /* Header — two fixed lines. Top row is identity + chrome (name, status,
          add menu, views, close) and never wraps, so the close X is always
          pinned top-right; category + tags get their own quiet line below. */ }
      <div className="px-4 pb-1.5 pt-3">
        <div className="flex items-center gap-2">
          <CollectionIcon className="size-3.5 shrink-0 text-muted-foreground"/>
          {renaming && !readOnly ? (<input autoFocus value={nameDraft} onChange={(e)=>setNameDraft(e.target.value)} onBlur={commitRename} onKeyDown={(e)=>{
        if (e.key === 'Enter') commitRename();
        if (e.key === 'Escape') setRenaming(false);
    }} className="min-w-0 flex-1 rounded bg-secondary px-1 py-0.5 text-sm font-semibold text-foreground outline-none"/>) : readOnly ? (<span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{c.name}</span>) : (<button onClick={()=>{
        setNameDraft(c.name);
        setRenaming(true);
    }} title={t("iXNbPf")} className="group/name flex min-w-0 items-center gap-1 truncate rounded text-sm font-semibold text-foreground">
              <span className="truncate">{c.name}</span>
              <Pencil className="size-3 shrink-0 text-transparent group-hover/name:text-muted-foreground"/>
            </button>)}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {!readOnly && (<Popover open={addOpen} onOpenChange={setAddOpen}>
                <PopoverTrigger asChild>
                  <button title={t("0t8z1W")} className="flex h-6 shrink-0 items-center gap-1 rounded-md bg-foreground px-2.5 text-[11px] font-medium text-background transition-opacity hover:opacity-85">
                    <Plus className="size-3"/>
                    {'Add'}
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="end" className="w-56 p-1">
                  <button onClick={()=>{
        setAddOpen(false);
        pickFiles();
    }} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-secondary">
                    <Upload className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75}/>
                    Upload assets…
                  </button>
                  <button onClick={()=>{
        setAddOpen(false);
        setDetailId(libraryActions.addTextFragment(id, {
            name: 'Fragment'
        }));
    }} title={t("62H3IK")} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-secondary">
                    <Braces className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75}/>
                    Add variable
                  </button>
                  <div className="my-1 border-t border-border"/>
                  <div className="px-2 pb-1 pt-0.5 text-[11px] leading-relaxed text-muted-foreground/60">
                    Or drag files and canvas assets straight in.
                  </div>
                </PopoverContent>
              </Popover>)}
            <button onClick={onClose} className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" aria-label={t("rbrahO")}>
              <X className="size-3.5"/>
            </button>
          </div>
        </div>
        { /* Description — free-form notes right under the title (identity reads
            top-down: what it is, then how it's filed), edited in place. */ }
        {descEditing && !readOnly ? (<textarea autoFocus data-quiet-focus defaultValue={c.description ?? ''} rows={2} onBlur={(e)=>{
        setCollectionDescription(id, e.target.value);
        setDescEditing(false);
    }} onKeyDown={(e)=>{
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === 'Escape') {
            // Reset then blur — the commit becomes a no-op.
            ;
            (e.target as HTMLTextAreaElement).value = c.description ?? '';
            (e.target as HTMLTextAreaElement).blur();
        }
    }} placeholder={t("sGJpuF")} className="mt-1.5 w-full resize-none rounded bg-secondary/60 px-2 py-1 text-xs leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/50"/>) : c.description ? (readOnly ? (<p className="mt-1.5 block w-full whitespace-pre-wrap text-left text-xs leading-relaxed text-muted-foreground">
              {c.description}
            </p>) : (<button onClick={()=>setDescEditing(true)} title={t("mOvlDe")} className="mt-1.5 block w-full whitespace-pre-wrap text-left text-xs leading-relaxed text-muted-foreground transition-colors hover:text-foreground">
              {c.description}
            </button>)) : (!readOnly && (<button onClick={()=>setDescEditing(true)} className="mt-1 block text-left text-[11px] text-muted-foreground/50 transition-colors hover:text-foreground">
              {t("sGJpuF")}
            </button>))}
        { /* Identity — tags on their own quiet line. */ }
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {c.tags.map((tag)=>(/* Standard Badge chip. The × floats over the corner on hover —
               no reserved space (that read as broken padding), no reflow. */ <Badge key={tag} variant="secondary" className="group/tag relative font-normal text-muted-foreground">
              {tag}
              {!readOnly && (<button onClick={()=>setCollectionTags(id, c.tags.filter((x)=>x !== tag))} title={t("NNbHjg")} aria-label={t("vSHXh4", {
            tag
        })} className="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full border border-border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-foreground group-hover/tag:opacity-100">
                  <X className="size-2.5"/>
                </button>)}
            </Badge>))}
          {!readOnly && (tagAdding ? (<span className="relative">
                <input data-quiet-focus autoFocus value={tagDraft} onChange={(e)=>{
        setTagDraft(e.target.value);
        setTagIdx(0);
    }} onKeyDown={(e)=>{
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setTagIdx((i)=>Math.min(i + 1, Math.max(0, tagOptionCount - 1)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setTagIdx((i)=>Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const picked = tagActiveIdx < tagSuggestions.length ? tagSuggestions[tagActiveIdx] : tagDraftNorm;
            if (picked) pickTag(picked);
        } else if (e.key === 'Escape') {
            setTagDraft('');
            setTagAdding(false);
        }
    }} onBlur={()=>// Blur cancels — typos shouldn't become vocabulary. Enter/click commit.
        setTimeout(()=>{
            setTagDraft('');
            setTagAdding(false);
        }, 120)} placeholder={t("7Jn935")} className="h-6 w-36 rounded-md border border-border/60 bg-secondary/60 px-2 text-xs text-foreground outline-none placeholder:text-muted-foreground/50 focus:border-border"/>
                {tagOptionCount > 0 && (<span className="absolute left-0 top-full z-20 mt-1 flex w-44 flex-col rounded-lg border border-border bg-popover p-1 shadow-xl">
                    {tagSuggestions.map((t, i)=>(<button key={t} onMouseDown={(e)=>{
            e.preventDefault();
            pickTag(t);
        }} onMouseEnter={()=>setTagIdx(i)} className={cn('flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors', i === tagActiveIdx ? 'bg-secondary text-foreground' : 'text-muted-foreground')}>
                        <span className="min-w-0 truncate">{t}</span>
                      </button>))}
                    {tagCreatable && (<button onMouseDown={(e)=>{
        e.preventDefault();
        pickTag(tagDraftNorm);
    }} onMouseEnter={()=>setTagIdx(tagSuggestions.length)} className={cn('flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors', tagActiveIdx === tagSuggestions.length ? 'bg-secondary text-foreground' : 'text-muted-foreground')}>
                        <Plus className="size-3 shrink-0 text-muted-foreground/60"/>
                        <span className="min-w-0 truncate">{t("L3IL25", {
        tag: tagDraftNorm
    })}</span>
                      </button>)}
                  </span>)}
              </span>) : (<button onClick={()=>setTagAdding(true)} className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground/60 transition-colors hover:bg-secondary hover:text-foreground">
                {t("WyJunX")}
              </button>))}
        </div>
      </div>

      { /* Meta — the identity line stays tiny: type + count + tags. Usage and
          reference hints live in the footer (info, not identity). */ }
      { /* Toolbar */ }
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-border px-4 pb-2.5 pt-1.5">
        <SegTabs value={effectiveView} onChange={setView} showLabels={effWidth >= 360} options={[
        {
            key: 'gallery',
            label: 'Gallery'
        },
        ...(SHOW_MATRIX ? [
            {
                key: 'matrix' as const,
                label: 'Matrix',
                disabled: !matrixAvailable
            }
        ] : []),
        {
            key: 'table',
            label: 'Table'
        }
    ]}/>
        <FilterControl facets={facetDefs} filters={filters} counts={counts} onChange={setFilters} compact={effWidth <= 420} labelFor={facetLabel}/>
        <div className="ml-auto flex items-center gap-2">
          {selection.size > 0 && (<span className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <span className="tabular-nums text-foreground">{selection.size}</span> {t("LtTjKV")}
              <button onClick={clearSelection} className={ghost}>
                {t("_GCoTA")}
              </button>
            </span>)}
          {filtered.length !== assets.length && (<span className="text-xs tabular-nums text-muted-foreground/60">
              {t("4mvNxY", {
        filtered: String(filtered.length),
        total: String(assets.length)
    })}
            </span>)}
          { /* Group-by works in gallery AND table — the view changes shape, not
              the question being asked of the assets. Hidden in the narrow editor
              column (a power move for the full /library view); keeps the toolbar
              to one row here. */ }
          {!narrow && (effectiveView === 'gallery' || effectiveView === 'table') && groupableProps.length > 0 && (<span title={t("HWWSQ_")}>
              <PropPicker value={groupBy} options={groupableProps} label={propLabel} onChange={setGroupBy} allowNone prefix="Group" menuLabel="Group by"/>
            </span>)}
          {effectiveView === 'table' && (<div className="flex items-center gap-1 rounded-md bg-secondary/40 p-0.5">
              <SegTabs className="bg-transparent p-0" value={gallerySize} onChange={setGallerySize} showLabels={false} options={[
        {
            key: 'Small',
            label: 'Compact rows',
            icon: (<div className="flex size-3 flex-col justify-between py-px">
                        {Array.from({
                length: 3
            }, (_, i)=>(<div key={i} className="h-px bg-current"/>))}
                      </div>)
        },
        {
            key: 'Medium',
            label: 'Comfortable rows',
            icon: (<div className="flex size-3 flex-col justify-between py-px">
                        {Array.from({
                length: 2
            }, (_, i)=>(<div key={i} className="h-[2px] bg-current"/>))}
                      </div>)
        },
        {
            key: 'Large',
            label: 'Roomy rows',
            icon: <div className="size-3 rounded-[1px] bg-current"/>
        }
    ]}/>
            </div>)}
          {effectiveView === 'gallery' && (<>
              { /* Crop + size read as ONE quiet cluster, not two floating controls.
                  In the narrow editor column, crop (square vs original) is a
                  power toggle not worth two buttons — square is the default; only
                  tile size stays. */ }
              <div className="flex items-center gap-1 rounded-md bg-secondary/40 p-0.5">
                {!narrow && (<>
                    <SegTabs className="bg-transparent p-0" value={galleryCrop} onChange={setGalleryCrop} showLabels={false} options={[
        {
            key: 'Square',
            label: 'Square tiles',
            icon: <div className="size-3 rounded-[2px] border border-current"/>
        },
        {
            key: 'Original',
            label: 'Original ratio',
            icon: <div className="h-2.5 w-3.5 rounded-[2px] border border-current"/>
        }
    ]}/>
                    <div className="h-3.5 w-px bg-border"/>
                  </>)}
                <SegTabs className="bg-transparent p-0" value={gallerySize} onChange={setGallerySize} showLabels={false} options={[
        {
            key: 'Small',
            label: 'Small tiles',
            icon: (<div className="grid size-3 grid-cols-3 gap-px">
                          {Array.from({
                length: 9
            }, (_, i)=>(<div key={i} className="bg-current"/>))}
                        </div>)
        },
        {
            key: 'Medium',
            label: 'Medium tiles',
            icon: (<div className="grid size-3 grid-cols-2 gap-px">
                          {Array.from({
                length: 4
            }, (_, i)=>(<div key={i} className="bg-current"/>))}
                        </div>)
        },
        {
            key: 'Large',
            label: 'Large tiles',
            icon: <div className="size-3 rounded-[1px] bg-current"/>
        }
    ]}/>
              </div>
            </>)}
        </div>
      </div>

      {effectiveView === 'matrix' && (<div className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border">
          <PropPicker value={rowKey} options={discriminating} exclude={colKey} label={propLabel} onChange={(v)=>setAxes([
            v,
            colKey
        ])}/>
          <button onClick={()=>setAxes([
            colKey,
            rowKey
        ])} title={t("9wjdXC")} className="rounded p-0.5 hover:bg-secondary hover:text-foreground">
            <ArrowLeftRight className="size-3"/>
          </button>
          <PropPicker value={colKey} options={discriminating} exclude={rowKey} label={propLabel} onChange={(v)=>setAxes([
            rowKey,
            v
        ])}/>
        </div>)}

      { /* Body — accepts OS file drops (HTML5 DnD) and canvas content drags
          (grip handle / ⌘-drag; mouse-based, consumed on mouseup). */ }
      { /* Positioning context for the slide-over asset peek (it overlays the
          gallery from the right edge and never reflows the content). */ }
      <div className="relative flex min-h-0 flex-1">
        <div onDragOver={(e)=>{
        if (readOnly) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragOver(true);
    }} onDragLeave={(e)=>{
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragOver(false);
    }} onDrop={(e)=>{
        if (readOnly) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragOver(false);
        onFiles(e.dataTransfer.files);
    }} ref={marqueeHost} onMouseDown={onMarqueeMouseDown} className={cn('relative min-h-0 flex-1 overflow-auto px-4 py-3', marqueeBox && 'select-none')}>
          {marqueeBox && (<div className="pointer-events-none absolute z-20 rounded-sm border border-foreground/40 bg-foreground/10" style={marqueeBox}/>)}
          {assets.length === 0 ? (readOnly ? (<div className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
                <p className="text-xs text-muted-foreground">No assets in this collection</p>
                <p className="text-[10px] text-muted-foreground/60">
                  Ask a team member to add assets to the shared Library.
                </p>
              </div>) : (/* Ghost previews: what this collection could hold, drawn as dashed
             placeholder tiles (the app's drop-target grammar). They spark the
             idea AND are the drop zone — click any to add. */ <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                <div className="grid w-full max-w-md grid-cols-2 gap-2 px-4 sm:grid-cols-4">
                  {GHOSTS.map((label)=>(<button key={label} onClick={pickFiles} className="flex aspect-square flex-col items-center justify-end gap-1 rounded-md border border-dashed border-border/80 p-2 text-muted-foreground/50 transition-colors hover:border-foreground/40 hover:text-muted-foreground">
                      <span className="flex flex-1 items-center justify-center">
                        <Plus className="size-4 opacity-40"/>
                      </span>
                      <span className="text-[10px] leading-tight">{label}</span>
                    </button>))}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-foreground/80">
                    {t("MM0TXM", {
        name: c.name
    })}
                  </p>
                  <p className="max-w-64 text-xs leading-relaxed text-muted-foreground">
                    {t("sWy2tI")}{' '}
                    <button onClick={pickFiles} className="text-foreground underline-offset-2 hover:underline">
                      {t("NOBm6L")}
                    </button>
                    .
                  </p>
                </div>
              </div>)) : effectiveView === 'gallery' ? (<GalleryView assets={filtered} size={gallerySize} crop={galleryCrop} groupBy={groupBy || null} groupProp={groupBy ? propByKey(groupBy) : undefined} groupValues={groupBy ? valuesPresent(filtered, groupBy) : []} groupLabel={groupBy ? propLabel(groupBy) : ''} valueLabel={(token)=>facetLabel(groupBy, token)} selection={selection} onToggle={toggleSel} onAddAsset={readOnly ? undefined : pickFiles} onAssetContext={readOnly ? undefined : openAssetCtx} onOpenAsset={setDetailId} onOpenAssetFull={openLightbox} readOnly={readOnly}/>) : effectiveView === 'matrix' ? (<MatrixView assets={filtered} rowKey={rowKey} colKey={colKey} rowValues={valuesPresent(filtered, rowKey)} colValues={valuesPresent(filtered, colKey)} rowLabel={propLabel(rowKey)} valueLabel={attrLabel} selection={selection} onAssetContext={readOnly ? undefined : openAssetCtx} onOpenAsset={setDetailId} onOpenAssetFull={openLightbox}/>) : (<TableView assets={filtered} columns={columns} properties={liveProps} groupBy={groupBy || undefined} groupProp={groupBy ? propByKey(groupBy) : undefined} groupValues={groupBy ? valuesPresent(filtered, groupBy) : []} size={gallerySize} propLabel={propLabel} columnOptions={propOptions} hiddenCount={hiddenCount} showingAll={showAllCols} onToggleCols={()=>setShowAllCols((v)=>!v)} onSetAttr={setAttr} onSetValueRaw={setAttrRaw} onRenameOption={libraryActions.renamePropertyOption} onAddProperty={onAddProperty} propertyTypesEnabled={propertyTypesEnabled} sort={sort} onSort={setSort} selection={selection} onToggle={toggleSel} onAssetContext={readOnly ? undefined : openAssetCtx} onOpenAsset={setDetailId} onOpenAssetFull={openLightbox} readOnly={readOnly}/>)}
        </div>
      </div>

      { /* Asset peek — a full-height side panel pinned to the right. The root
          reserves its width as padding, so the collection view shrinks left
          and the panel never covers the toolbar / view controls. */ }
      {detailId && (()=>{
        const a = assets.find((x)=>x.id === detailId);
        if (!a) return null;
        return (<AssetDetailPanel key={`${a.id}:${initialAssetRevealNonce ?? ''}`} asset={a} highlighted={initialAssetId === a.id && initialAssetRevealNonce != null} narrow={narrow} collectionName={c.name} isCover={c.heroAssetId === a.id} properties={usedProps} propertyTypesEnabled={propertyTypesEnabled} width={peekWidth} onWidth={setPeekWidth} onOpenFull={()=>openLightbox(a.id)} onRename={(name)=>libraryActions.renameAsset(a.id, name)} onSetValueByLabel={(key, value)=>setAttr(a.id, key, value)} onSetValue={(key, value)=>setAttrRaw(a.id, key, value)} onAddProperty={onAddProperty} onAddPropertyWithValue={(label, value)=>{
            libraryActions.addPropertyWithValue(a.id, label, value);
            setShowAllCols(true); // fresh property is non-discriminating; reveal it
        }} onUseAsCover={()=>setHero(id, a.id)} onDelete={()=>{
            deleteAssets([
                a.id
            ]);
            setDetailId(null);
        }} onClose={()=>setDetailId(null)} readOnly={readOnly}/>);
    })()}

      {!readOnly && dragOver && (<div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-foreground/40 bg-background/70">
          <span className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground shadow-md">
            {t("A4t1l4", {
        name: c.name
    })}
          </span>
        </div>)}

      {!readOnly && assetCtx && typeof document !== 'undefined' && createPortal(<>
            <div className="fixed inset-0 z-[70]" onClick={closeCtx} onContextMenu={(e)=>{
        e.preventDefault();
        closeCtx();
    }}/>
            <div style={{
        left: Math.min(assetCtx.x, window.innerWidth - 208),
        top: Math.min(assetCtx.y, window.innerHeight - 300)
    }} className="fixed z-[71] w-48 rounded-md border border-border bg-popover p-1 text-xs shadow-xl">
              {ctxView === 'root' && (<>
                  <div className="px-2 pb-1 pt-0.5 text-[10px] tabular-nums text-muted-foreground/60">
                    {ctxIds.length === 1 ? t("-9Pa7L") : t("KMSLKQ", {
        count: String(ctxIds.length)
    })}
                  </div>
                  {ctxIds.length === 1 && (<button onClick={()=>{
        setHero(id, ctxIds[0]);
        closeCtx();
    }} className="block w-full rounded px-2 py-1 text-left text-foreground hover:bg-secondary">
                      {t("O4k2Fd")}
                    </button>)}
                  {orderedProps.length > 0 && (<button onClick={()=>setCtxView('props')} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-foreground hover:bg-secondary">
                      {t("Cgbb8W")} <ChevronRight className="size-3 shrink-0 text-muted-foreground/50"/>
                    </button>)}
                  <button onClick={()=>setCtxView('move')} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-foreground hover:bg-secondary">
                    {t("AfxJ0y")} <ChevronRight className="size-3 shrink-0 text-muted-foreground/50"/>
                  </button>
                  <div className="my-1 border-t border-border"/>
                  <button onClick={ctxRemove} className="block w-full rounded px-2 py-1 text-left text-destructive hover:bg-destructive/10">
                    {t("G_yZLu")} {ctxIds.length > 1 ? ctxIds.length : ''}
                  </button>
                </>)}
              {ctxView === 'props' && (<>
                  <button onClick={()=>setCtxView('root')} className="mb-0.5 block w-full rounded px-2 py-1 text-left text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                    {t("Vug_4M")}
                  </button>
                  {orderedProps.map((k)=>(<button key={k} onClick={()=>setCtxView({
                prop: k
            })} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-foreground hover:bg-secondary">
                      <span className="truncate">{propLabel(k)}</span>
                      <ChevronRight className="size-3 shrink-0 text-muted-foreground/50"/>
                    </button>))}
                </>)}
              {typeof ctxView === 'object' && (<>
                  <button onClick={()=>setCtxView('props')} className="mb-0.5 block w-full rounded px-2 py-1 text-left text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                    {t("UvLeCg", {
        prop: propLabel(ctxView.prop)
    })}
                  </button>
                  {propOptions(ctxView.prop).map((o)=>o.value).map((v)=>(<button key={v} onClick={()=>{
            applyPropTo(ctxIds, ctxView.prop, v);
            closeCtx();
        }} className="block w-full truncate rounded px-2 py-1 text-left text-foreground hover:bg-secondary">
                        {v}
                      </button>))}
                  <button onClick={()=>{
        applyPropTo(ctxIds, ctxView.prop, null);
        closeCtx();
    }} className="block w-full rounded px-2 py-1 text-left text-muted-foreground hover:bg-secondary">
                    {t("seboF3")}
                  </button>
                </>)}
              {ctxView === 'move' && (<>
                  <button onClick={()=>setCtxView('root')} className="mb-0.5 block w-full rounded px-2 py-1 text-left text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                    {t("zChHL8")}
                  </button>
                  <div className="max-h-56 overflow-auto">
                    {otherCollections.length === 0 && (<div className="px-2 py-1 text-[11px] text-muted-foreground/50">{t("z1EGM4")}</div>)}
                    {otherCollections.map((x)=>(<button key={x.id} onClick={()=>ctxMove(x.id)} className="block w-full truncate rounded px-2 py-1 text-left text-foreground hover:bg-secondary">
                        {x.name}
                      </button>))}
                  </div>
                </>)}
            </div>
          </>, document.body)}

      { /* Footer — info, not identity: usage graph + @reference hints. */ }
      <div className="flex flex-wrap items-center gap-x-1.5 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground border-t border-border">
        {showUsage && shots.length === 0 && (<span className="flex items-center gap-0.5 text-foreground/80">
            {t("dROGNi")} <span className="tabular-nums">{c.usedIn}</span> {c.usedIn === 1 ? t("eTlcMj") : t("mmoMkH")}
          </span>)}
        {showUsage && shots.length > 0 && (<>
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-0.5 text-foreground/80 hover:text-foreground">
                  {t("dROGNi")} <span className="tabular-nums">{c.usedIn}</span>{' '}
                  {c.usedIn === 1 ? t("eTlcMj") : t("mmoMkH")}
                  <ChevronDown className="size-3"/>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-0">
                <div className="px-2.5 py-1.5 text-[11px] text-muted-foreground border-b border-border">
                  {t("aaYSiV")} <span className="tabular-nums">{c.usedIn}</span>{' '}
                  {c.usedIn === 1 ? t("eTlcMj") : t("mmoMkH")}
                </div>
                <div className="max-h-72 overflow-auto p-1">
                  {[
        ...new Set(shots.map((s)=>s.project))
    ].map((film)=>(<div key={film}>
                      <div className="px-1.5 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                        {film}
                      </div>
                      {shots.filter((s)=>s.project === film).map((s)=>(<button key={s.i} title={t("pNJ2yS")} className="group flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-secondary">
                            {assets.length > 0 && (<div className="size-7 shrink-0 overflow-hidden rounded border border-border">
                                <img src={getAssetUrl(assets[(s.i + 5) % assets.length])} alt="" loading="lazy" className="h-full w-full object-cover"/>
                              </div>)}
                            <span className="min-w-0 flex-1 truncate text-xs text-foreground/90">{s.shot}</span>
                            <ChevronRight className="size-3 shrink-0 text-transparent group-hover:text-muted-foreground"/>
                          </button>))}
                    </div>))}
                </div>
                <div className="px-2.5 py-1.5 text-[10px] leading-relaxed text-muted-foreground/60 border-t border-border">
                  {t("3aXtBv")}
                </div>
              </PopoverContent>
            </Popover>
          </>)}
        { /* eslint-disable-next-line i18next/no-literal-string -- punctuation separator */ }
        {showUsage && <span className="text-muted-foreground/40">·</span>}
        <span>
          {t("SM7N52")} <code className="rounded bg-muted px-1 text-foreground">@{c.name}</code>
          {sliceVal && (<>
              {t("KgwEsQ")}{' '}
              <code className="rounded bg-muted px-1 text-foreground">
                @{c.name}/{sliceVal}
              </code>
            </>)}
        </span>
      </div>

      {!readOnly && (<input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e)=>{
        onFiles(e.target.files);
        e.target.value = '';
    }}/>)}

      {lightbox && (()=>{
        const idx = filtered.findIndex((a)=>a.id === lightbox);
        if (idx === -1) return null;
        return (<AssetLightbox valueLabel={attrLabel} assets={filtered} index={idx} heroAssetId={c.heroAssetId} onStep={(i)=>setLightbox(filtered[i]?.id ?? null)} onClose={()=>setLightbox(null)} onSetHero={readOnly ? undefined : (aid)=>setHero(id, aid)}/>);
    })()}
    </div>);
}
